// Policy Details section for the Custom Disclaimers setup and assignment states. Permission
// status comes from runtime config, while assignment status reuses the disclaimer list already
// loaded by the protected shell so this section does not add a duplicate API request.

import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Spinner, Text } from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useDisclaimerEnrollmentSummary } from '../../contexts/DisclaimerEnrollmentSummaryContext';
import { useGeneralConfig } from '../../hooks/useGeneralConfig';
import { customDisclaimerService } from '../../services/customDisclaimerService';
import type { CustomDisclaimer } from '../../types/customDisclaimers';
import { CUSTOM_DISCLAIMERS_REQUIRED_PERMISSIONS } from '../../constants/customDisclaimers';
import { DisclaimerPreviewDialog } from './DisclaimerPreviewDialog';
import { useStyles } from './PolicyDisclaimerSection.style';

export const CUSTOM_DISCLAIMERS_ROUTE = '/custom-disclaimers';

type DisclaimerConsentStatus = 'checking' | 'unknown' | 'granted' | 'required';

const normalizePolicyReference = (value: string): string => value.trim().toLowerCase();

const findAssignedDisclaimer = (
  disclaimers: readonly CustomDisclaimer[],
  consentStatus: DisclaimerConsentStatus,
  policyId: string | undefined,
): CustomDisclaimer | undefined => {
  if (consentStatus !== 'granted' || !policyId) {
    return undefined;
  }

  const normalizedPolicyReference = normalizePolicyReference(policyId);
  return disclaimers.find((disclaimer) => {
    const matchesPolicyId = disclaimer.policyIds.some(
      (assignedPolicyId) => normalizePolicyReference(assignedPolicyId) === normalizedPolicyReference,
    );
    const matchesPolicy = disclaimer.policies.some(
      (policy) =>
        normalizePolicyReference(policy.id) === normalizedPolicyReference ||
        normalizePolicyReference(policy.name) === normalizedPolicyReference,
    );

    return matchesPolicyId || matchesPolicy;
  });
};

export const PolicyDisclaimerSection: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id: policyId } = useParams<{ id: string }>();
  const { config, loadingConfig, loadConfig } = useGeneralConfig();
  const { disclaimerState, disclaimers, refreshDisclaimers } = useDisclaimerEnrollmentSummary();
  const [previewDisclaimer, setPreviewDisclaimer] = useState<CustomDisclaimer | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewRequestRef = useRef(0);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    previewRequestRef.current += 1;
    setPreviewDisclaimer(null);
    setPreviewLoading(false);
    setPreviewError(null);
  }, [policyId]);

  const azure = config?.applications?.backend?.azure;
  const grantedPermissions = new Set((azure?.apiPermissions ?? []).map((permission) => permission.toLowerCase()));
  const hasDisclaimerPermission = CUSTOM_DISCLAIMERS_REQUIRED_PERMISSIONS.every((permission) =>
    grantedPermissions.has(permission.toLowerCase()),
  );

  // useGeneralConfig swallows load failures and leaves config null, so that means "unknown"
  // (a failed lookup), matching the Custom Disclaimers overview page's own derivation.
  let consentStatus: DisclaimerConsentStatus;
  if (loadingConfig) {
    consentStatus = 'checking';
  } else if (config) {
    consentStatus = hasDisclaimerPermission ? 'granted' : 'required';
  } else {
    consentStatus = 'unknown';
  }

  if (consentStatus === 'checking') {
    return (
      <div className={styles.stateContainer} data-testid="policy-disclaimer-section-loading">
        <Spinner size="tiny" label="Checking disclaimer setup..." />
      </div>
    );
  }

  if (consentStatus === 'unknown') {
    return (
      <div className={styles.stateContainer} data-testid="policy-disclaimer-section-error">
        <Text>Could not determine whether disclaimers are set up. Please try again.</Text>
        <Button appearance="secondary" size="small" onClick={loadConfig}>
          Try again
        </Button>
      </div>
    );
  }

  if (consentStatus === 'granted' && disclaimerState === 'loading') {
    return (
      <div className={styles.stateContainer} data-testid="policy-disclaimer-section-assignment-loading">
        <Spinner size="tiny" label="Checking disclaimer assignment..." />
      </div>
    );
  }

  if (consentStatus === 'granted' && disclaimerState === 'error') {
    return (
      <div className={styles.stateContainer} data-testid="policy-disclaimer-section-assignment-error">
        <Text>Could not determine whether a disclaimer is assigned to this policy. Please try again.</Text>
        <Button appearance="secondary" size="small" onClick={refreshDisclaimers}>
          Try again
        </Button>
      </div>
    );
  }

  const assignedDisclaimer = findAssignedDisclaimer(disclaimers, consentStatus, policyId);

  const handleGeneratePreview = async () => {
    if (!assignedDisclaimer) {
      return;
    }

    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const currentDisclaimer = await customDisclaimerService.getById(assignedDisclaimer.id);
      if (requestId !== previewRequestRef.current) {
        return;
      }
      if (!currentDisclaimer) {
        throw new Error('Assigned disclaimer was not found.');
      }
      setPreviewDisclaimer(currentDisclaimer);
    } catch {
      if (requestId === previewRequestRef.current) {
        setPreviewError('Unable to generate the disclaimer preview. Please try again.');
      }
    } finally {
      if (requestId === previewRequestRef.current) {
        setPreviewLoading(false);
      }
    }
  };

  if (assignedDisclaimer) {
    return (
      <>
        <Card
          className={styles.assignedCard}
          style={{ padding: '24px', gap: '24px' }}
          data-testid="policy-disclaimer-section-card"
        >
          <div className={styles.assignedContent}>
            <Text as="h2" className={styles.assignedTitle} weight="semibold">
              Disclaimers
            </Text>
            <Text className={styles.messageText} data-testid="policy-disclaimer-section-assigned-message">
              <span className={styles.assignedName}>{assignedDisclaimer.name}</span> is assigned to this policy.
            </Text>
          </div>
          <div className={styles.assignedActions}>
            <Button
              appearance="secondary"
              className={styles.ctaButton}
              disabled={previewLoading}
              onClick={handleGeneratePreview}
              data-testid="policy-disclaimer-section-preview-button"
            >
              Generate preview
            </Button>
            <Button
              appearance="secondary"
              className={styles.ctaButton}
              onClick={() => navigate(CUSTOM_DISCLAIMERS_ROUTE)}
              data-testid="policy-disclaimer-section-manage-button"
            >
              Manage disclaimers
            </Button>
          </div>
          {previewError && (
            <Text className={styles.previewError} role="alert">
              {previewError}
            </Text>
          )}
        </Card>
        {previewDisclaimer && (
          <DisclaimerPreviewDialog
            open
            onClose={() => setPreviewDisclaimer(null)}
            organisationName={previewDisclaimer.organisationName}
            text={previewDisclaimer.text}
          />
        )}
      </>
    );
  }

  if (consentStatus === 'granted') {
    return (
      <div
        className={styles.assignmentCard}
        style={{
          padding: '24px',
          gap: '32px',
          border: 'none',
          borderRadius: '6px',
          boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
        }}
        data-testid="policy-disclaimer-section-card"
      >
        <div className={styles.assignmentContent}>
          <Text as="h2" className={styles.assignmentTitle} weight="semibold">
            Disclaimers status
          </Text>
          <div className={styles.messageRow} style={{ gap: '4px' }}>
            <InfoRegular
              className={styles.assignmentInfoIcon}
              role="img"
              aria-label="Information"
              data-testid="policy-disclaimer-section-info-icon"
            />
            <Text className={styles.messageText} style={{ fontSize: '14px', lineHeight: '20px' }}>
              No disclaimer assigned to this policy.
            </Text>
          </div>
        </div>
        <Button
          appearance="secondary"
          className={styles.ctaButton}
          onClick={() => navigate(CUSTOM_DISCLAIMERS_ROUTE)}
          data-testid="policy-disclaimer-section-manage-button"
        >
          Manage disclaimers
        </Button>
      </div>
    );
  }

  return (
    <Card className={styles.card} data-testid="policy-disclaimer-section-card">
      <Text as="h2" className={styles.title} weight="semibold">
        Disclaimers
      </Text>
      <div className={styles.messageRow}>
        <InfoRegular
          className={styles.infoIcon}
          role="img"
          aria-label="Information"
          data-testid="policy-disclaimer-section-info-icon"
        />
        <Text className={styles.messageText}>
          Disclaimers are not set up. To use disclaimers with policies, first enable the required Microsoft Graph
          permissions.
        </Text>
      </div>
      <Button
        appearance="secondary"
        className={styles.ctaButton}
        onClick={() => navigate(CUSTOM_DISCLAIMERS_ROUTE)}
        data-testid="policy-disclaimer-section-cta-button"
      >
        Set up disclaimers
      </Button>
    </Card>
  );
};
